import { AsyncPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { BaseSiteService, Product, ProductScope, ProductService } from '@spartacus/core';
import { CurrentProductService } from '@spartacus/storefront';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, pairwise, startWith, switchMap, take, tap } from 'rxjs/operators';
import { VariantType } from 'src/app/model/product.model';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { CustomProductService } from 'src/app/service/product/custom-product.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { VariantSizeSelectorComponent } from './variant-size-selector/variant-size-selector.component';

export interface ShimanoProduct extends Product {
  superCategories?: any;
}
@Component({
  selector: 'app-product-variants',
  templateUrl: './product-variants.component.html',
  styleUrls: ['./product-variants.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [VariantSizeSelectorComponent, AsyncPipe],
})
export class ProductVariantsComponent implements OnInit {
  defaultVariantCategory = 'cgvvc_SHIC_default_default';
  defaultSuperCategory: 'cgvc_SHIC_default';
  baseSite$: Observable<string> = this.baseService.getActive();
  default = 'default';
  loading = false;
  isDesktop = false;

  constructor(
    private currentProductService: CurrentProductService,
    private cdr: ChangeDetectorRef,
    private baseService: BaseSiteService,
    private deviceDetectorService: DeviceDetectorService,
    private http: HttpClient,
    private componentService: ComponentService,
    private customProductService: CustomProductService,
    private languageService: ShimanoLanguageService,
    private productService: ProductService,
    private storageService: StorageService
  ) {
    this.customProductService
      .getSkipVariant()
      .pipe(take(1))
      .subscribe((resp) => {
        this.defaultSuperCategory = resp[this.baseSiteId]
          ? resp[this.baseSiteId].defaultSuperCategory
          : resp[this.default].defaultSuperCategory;
        this.defaultVariantCategory = resp[this.baseSiteId]
          ? resp[this.baseSiteId].defaultVariantCategory
          : resp[this.default].defaultVariantCategory;
      });
  }

  variants: any = [];
  variantsKeys: any = [];
  selectedCategoryGroup: any = [];
  variantType = VariantType;
  selectedVariantIndex: number;
  selectedVariant: string;
  product$: Observable<ShimanoProduct>;
  singleOption = false;
  prodCode: any;
  isLanguageChanged = false;

  get baseSiteId(): string {
    let baseSiteId: string;
    this.baseSite$.pipe(take(1)).subscribe((baseSiteData) => (baseSiteId = baseSiteData));
    return baseSiteId;
  }

  ngOnInit(): void {
    this.init(null);
  }
  init(code: string = null) {
    this.componentService.setIsAddToCartDisabled(true);
    this.isDesktop = this.deviceDetectorService.isDesktop();
    this.product$ = combineLatest([
      this.languageService.getActive().pipe(startWith(null), pairwise()),
      code === null ? this.currentProductService.getProduct() : this.productService.get(code, ProductScope.DETAILS),
    ]).pipe(
      switchMap(([[prevLanguage, currentLanguage], product]) => {
        prevLanguage !== currentLanguage ? (this.isLanguageChanged = true) : (this.isLanguageChanged = false);
        return of(product);
      }),
      filter((product) => !!(product && product.baseOptions)),
      distinctUntilChanged(),
      tap((product) => {
        if (this.prodCode !== product.code || this.isLanguageChanged) {
          this.update();
          this.variants = [];
          this.variantsKeys = [];
          const selectedVariant = (element: any) => element.variantOption.code === product.code;
          if (typeof product.variantMatrix !== 'undefined') {
            this.selectedVariantIndex = product.variantMatrix.findIndex(selectedVariant);
            this.selectedVariant = product.variantMatrix.find(selectedVariant).variantOption.code;
            this.flattenOptions(product.variantMatrix, product.baseOptions[0].options, null, []);
          }
          if (this.variantsKeys.length === 1) {
            this.singleOption = this.variantsKeys.includes(this.defaultVariantCategory);
          } else {
            this.singleOption = false;
          }
          if (Object.keys(this.variants).length > 2) {
            this.storageService.setSessionStorageItem('disableOrderFormsProductPage', 'false');
          } else {
            this.storageService.setSessionStorageItem('disableOrderFormsProductPage', 'true');
          }
          this.prodCode = product.code;
          this.loading = false;
          this.componentService.setIsLoadAddToCartDisabled(false);
          this.cdr.detectChanges();
        }
      })
    );
  }

  flattenOptions(matrixElement: any, baseOptions: any, overrideIndex: any, parentCategory: any) {
    matrixElement.forEach((option: any, index: any) => {
      if (option && option.variantValueCategory) {
        const optionFromBase = {
          option: baseOptions.filter((opt: any) => {
            return opt.code === option.variantOption.code;
          })[0],
        };
        if (option.variantOption.code === this.selectedVariant) {
          this.selectedCategoryGroup.push(option.variantValueCategory.name);
        }
        const combine = {
          ...option.variantOption,
          ...option.variantValueCategory,
          ...{ hasImage: option.parentVariantCategory.hasImage },
          ...optionFromBase,
          ...{ indexSelected: overrideIndex !== null ? overrideIndex : index },
          ...{ variantSelected: option.variantOption.code === this.selectedVariant ? true : false },
          ...{ categorySelected: this.selectedCategoryGroup },
          ...{ prevCategory: parentCategory.length > 0 ? parentCategory : [] },
        };
        const tempArray = this.variants[option.parentVariantCategory.name] || [];
        this.variants[option.parentVariantCategory.name] = [...tempArray, combine];
        if (!this.variantsKeys.includes(option.parentVariantCategory.name)) {
          if (option.parentVariantCategory.name !== 'N/A') {
            this.variantsKeys.push(option.parentVariantCategory.name);
          }
        }

        this.variants[option.parentVariantCategory.name].sort((a: any, b: any) => (a.sequence > b.sequence ? 1 : -1));

        if (option.elements.length > 0) {
          let parentName = [];
          if (parentCategory.length > 0) {
            parentName = parentCategory.concat([option.variantValueCategory.name]);
          } else {
            parentName = [option.variantValueCategory.name];
          }

          this.flattenOptions(option.elements, baseOptions, index, parentName);
        }
      }
    });
    this.cdr.detectChanges();
  }

  nonDefaultCategories(categories: any) {
    if (
      categories.filter(
        (cat: any) => cat.code === this.defaultSuperCategory || cat.code === this.defaultVariantCategory
      ).length > 0
    ) {
      return false;
    } else {
      return true;
    }
  }

  update() {
    this.selectedCategoryGroup = [];
    this.variantsKeys = [];
    this.variants = [];
  }
  reload(code: any) {
    this.init(code);
  }
  isLoading() {
    this.loading = true;
    this.componentService.setIsLoadAddToCartDisabled(true);
    this.cdr.detectChanges();
  }
}
